"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _APICommands = require("../constants/APICommands");
var _dcCore = require("dc-core");
var _ServiceRegistry = _interopRequireDefault(require("../util/ServiceRegistry"));
var _AppUtil = require("../util/AppUtil");
var _downloadFile = _interopRequireDefault(require("../preview/actions/downloadFile"));
var _saveFile = _interopRequireDefault(require("../preview/actions/saveFile"));
var _ApiConstants = _interopRequireDefault(require("../constants/ApiConstants"));
var _ErrorUtils = require("../util/ErrorUtils");
var _SearchModel = _interopRequireDefault(require("../model/SearchModel"));
var _Events = require("../constants/Events");
var _ViewMode = require("../constants/ViewMode");
var _mobx = require("mobx");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); } /*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
* Copyright 2020 Adobe
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/
const THUMBNAIL_OPTIONS = {
  THUMBNAIL_ZOOM_LEVEL: 0.2,
  THUMBNAIL_FORMATS: ["png"]
};
class PreviewCommands {
  constructor() {
    /**
     * @method
     * @description
     * This API is used to get all the bookmarks residing in the PDF file.
     *
     * @return {Promise} - promise that resolves with array of Bookmark Object
    */
    this._getBookmarks = () => new Promise((resolve, reject) => {
      const {
        enableBookmarkAPIs
      } = _ServiceRegistry.default.getService("AppStore").getActionConfig("preview");
      if (enableBookmarkAPIs) {
        this._publicAPIs.document.getBookmarks().then(bookmarkList => {
          resolve((0, _mobx.toJS)(bookmarkList));
        }).catch(() => {
          reject("There's some problem while processing your request.");
        });
      } else {
        reject("Bookmark apis is disabled");
      }
    });
    /**
     * @method
     * @description
     * This API is used to open the bookmark residing in the PDF file.
     *
     * @param id bookmarkId
     *
     * @return {Promise} - promise that resolves with boolean
     */
    this._openBookmark = id => {
      const {
        enableBookmarkAPIs
      } = _ServiceRegistry.default.getService("AppStore").getActionConfig("preview");
      if (enableBookmarkAPIs) {
        const returnVal = this._publicAPIs.document.executeBookmark(id) || {};
        return Promise.resolve(!!returnVal.success);
      }
      return Promise.reject("Bookmark apis is disabled");
    };
    /**
     * @method
     * @description
     * This API is used to get all the attachments residing in the PDF file.
     *
     * @return {Promise} - promise that resolves with array of Attachment Objects.
    */
    this._getAttachments = () => new Promise((resolve, reject) => {
      const {
        enableAttachmentAPIs
      } = _ServiceRegistry.default.getService("AppStore").getActionConfig("preview");
      if (enableAttachmentAPIs) {
        this._publicAPIs.document.getAttachments().then(attachmentList => {
          const updatedList = attachmentList.map(row => ({
            name: row.name,
            description: row.description,
            mimeType: row.mimeType,
            created: row.creationDate,
            modified: row.modifiedDate
          }));
          resolve((0, _mobx.toJS)(updatedList));
        }).catch(() => {
          reject("There's some problem while processing your request.");
        });
      } else {
        reject("Attachment apis is disabled");
      }
    });
    /**
     * @method
     * @description
     * This API is used to get the attachment residing in the PDF file.
     *
     * @param name fileName
     *
     * @return {Promise} - promise that resolves with Attachment Buffer Object.
     */
    this._getAttachmentBuffer = name => new Promise((resolve, reject) => {
      const {
        enableAttachmentAPIs
      } = _ServiceRegistry.default.getService("AppStore").getActionConfig("preview");
      if (enableAttachmentAPIs) {
        this._publicAPIs.document.getAttachmentBuffer(name).then(AttachmentBufferObject => {
          resolve((0, _mobx.toJS)(AttachmentBufferObject));
        }).catch(() => {
          reject("There's some problem while processing your request.");
        });
      } else {
        reject("Attachment apis is disabled");
      }
    });
    this._apiCommands = {
      [_APICommands.PreviewAPICommands.SWITCH_PREVIEW]: {
        action: this._switchPreview.bind(this)
      },
      [_APICommands.PreviewAPICommands.DOWNLOAD_VISIBILITY]: {
        validator: this._validateBooleanData.bind(this),
        action: this._toggleDownload.bind(this)
      },
      [_APICommands.PreviewAPICommands.FULL_SCREEN_VISIBILITY]: {
        validator: this._validateBooleanData.bind(this),
        action: this._toggleFullScreen.bind(this)
      },
      [_APICommands.PreviewAPICommands.PRINT_VISIBILITY]: {
        validator: this._validateBooleanData.bind(this),
        action: this._togglePrint.bind(this)
      },
      [_APICommands.PreviewAPICommands.COMMENTS_PANEL_VISIBILITY]: {
        validator: this._validateBooleanData.bind(this),
        action: this._toggleCommentPanel.bind(this)
      },
      [_APICommands.PreviewAPICommands.PAGE_NAVIGATION_CONTROL_VISIBILITY]: {
        validator: this._validateBooleanData.bind(this),
        action: this._togglePageNavControl.bind(this)
      },
      [_APICommands.PreviewAPICommands.PAGE_NUMBER_CONTROL_VISIBILITY]: {
        validator: this._validateBooleanData.bind(this),
        action: this._togglePageNumControl.bind(this)
      },
      [_APICommands.PreviewAPICommands.DOCK_SWITCH_VISIBILITY]: {
        validator: this._validateBooleanData.bind(this),
        action: this._toggleDockSwitch.bind(this)
      },
      [_APICommands.PreviewAPICommands.ZOOM_CONTROL_VISIBILITY]: {
        validator: this._validateBooleanData.bind(this),
        action: this._toggleZoomControl.bind(this)
      },
      [_APICommands.PreviewAPICommands.FIT_PAGE_VISIBILITY]: {
        validator: this._validateBooleanData.bind(this),
        action: this._toggleFitPage.bind(this)
      },
      [_APICommands.PreviewAPICommands.FIT_WIDTH_VISIBILITY]: {
        validator: this._validateBooleanData.bind(this),
        action: this._toggleFitWidth.bind(this)
      },
      [_APICommands.PreviewAPICommands.TWO_COLUMN_VISIBILITY]: {
        validator: this._validateBooleanData.bind(this),
        action: this._toggleTwoColumn.bind(this)
      },
      [_APICommands.PreviewAPICommands.FULL_SCREEN_BUTTON_VISIBILITY]: {
        validator: this._validateBooleanData.bind(this),
        action: this._toggleFullScreenButton.bind(this)
      },
      [_APICommands.PreviewAPICommands.EXIT_FULL_SCREEN_MODE]: {
        action: this._exitFullScreenMode.bind(this)
      },
      [_APICommands.PreviewAPICommands.SCRUBBER_VISIBILITY]: {
        validator: this._validateBooleanData.bind(this),
        action: this._toggleScrubber.bind(this)
      },
      [_APICommands.PreviewAPICommands.FILE_NAME_TOAST_VISIBILITY]: {
        validator: this._validateBooleanData.bind(this),
        action: this._toggleFileNameToast.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_SELECTED_ASSET]: {
        action: this._setSelectedAsset.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_VIEW_MODE]: {
        validator: this._validateStringData.bind(this),
        action: this._setViewMode.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_VIEW_MODE]: {
        action: this._getViewMode.bind(this)
      },
      [_APICommands.PreviewAPICommands.HUD_VISIBILITY]: {
        validator: this._validateBooleanData.bind(this),
        action: this._toggleHUD.bind(this)
      },
      [_APICommands.PreviewAPICommands.LEFT_PANE_VISIBILITY]: {
        validator: this._validateBooleanData.bind(this),
        action: this._toggleLHP.bind(this)
      },
      [_APICommands.PreviewAPICommands.RIGHT_PANE_VISIBILITY]: {
        validator: this._validateBooleanData.bind(this),
        action: this._toggleRHP.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_LEGAL_DIALOG_OPEN]: {
        validator: this._validateBooleanData.bind(this),
        action: this._setLegalDialogOpen.bind(this)
      },
      [_APICommands.PreviewAPICommands.TOGGLE_TEXT_SELECTION]: {
        validator: this._validateBooleanData.bind(this),
        action: this._toggleTextSelection.bind(this)
      },
      [_APICommands.PreviewAPICommands.ENABLE_INTERACTIVE_TOOLTIP]: {
        validator: this._validateBooleanData.bind(this),
        action: this._enableInteractiveTooltip.bind(this)
      },
      [_APICommands.PreviewAPICommands.DOCKED_HUD]: {
        validator: this._validateBooleanData.bind(this),
        action: this._dockHUD.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_PAGE_ZOOM_LEVEL]: {
        validator: this._validatePositiveNumberData.bind(this),
        action: this._getPageZoom.bind(this)
      },
      [_APICommands.PreviewAPICommands.CLEAR_PAGE_SELECTION]: {
        validator: this._validatePositiveNumberData.bind(this),
        action: this._clearPageSelection.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_CURSOR]: {
        validator: this._validateStringData.bind(this),
        action: this._setCursor.bind(this)
      },
      [_APICommands.PreviewAPICommands.GOTO_LOCATION]: {
        validator: this._validateGoToData.bind(this),
        action: this._gotoLocation.bind(this)
      },
      [_APICommands.PreviewAPICommands.ADD_SPACE_BELOW_DOCUMENT_IN_MOBILE]: {
        validator: this._validateAddSpaceBelowDocumentData.bind(this),
        action: this._addBottomSpaceBelowDocumentInMobile.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_XMP_METADATA]: {
        validator: this._validateXMPData.bind(this),
        action: this._getXMPMetadata.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_ZOOM_LEVEL]: {
        validator: this._validatePositiveFloatData.bind(this),
        action: this._setZoomLevel.bind(this)
      },
      [_APICommands.PreviewAPICommands.START_SEARCH]: {
        validator: this._validateSearchData.bind(this),
        action: this._searchQuery.bind(this)
      },
      [_APICommands.PreviewAPICommands.PERFORM_SEARCH]: {
        validator: this._validatePerformSearchData.bind(this),
        action: this._performSearch.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_THUMBNAIL]: {
        validator: this._validateThumbnailsData.bind(this),
        action: this._getThumbnails.bind(this)
      },
      [_APICommands.PreviewAPICommands.OPEN_BOOKMARK]: {
        validator: this._validateStringData.bind(this),
        action: this._openBookmark.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_ATTACHMENT_BUFFER]: {
        validator: this._validateStringData.bind(this),
        action: this._getAttachmentBuffer.bind(this)
      },
      [_APICommands.PreviewAPICommands.REGISTER_BEFORE_RHP_OPEN_CALLBACK]: {
        validator: this._validateHookData.bind(this),
        action: this._registerBeforeRHPOpenCallback.bind(this)
      },
      [_APICommands.PreviewAPICommands.REGISTER_BEFORE_QUICK_TOOL_ACTIVATED_CALLBACK]: {
        validator: this._validateHookData.bind(this),
        action: this._registerBeforeQuickToolActivatedCallback.bind(this)
      },
      [_APICommands.PreviewAPICommands.REGISTER_BEFORE_STICKY_TOOL_ACTIVATED_CALLBACK]: {
        validator: this._validateHookData.bind(this),
        action: this._registerBeforeStickyToolActivatedCallback.bind(this)
      },
      [_APICommands.PreviewAPICommands.SHOW_COACH_MARK]: {
        action: this._showCoachMark.bind(this)
      },
      [_APICommands.PreviewAPICommands.RESET_QUICK_TOOL]: {
        action: this._resetQuickTool.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_ACTIVE_QUICK_TOOL]: {
        action: this._getActiveQuickTool.bind(this)
      },
      [_APICommands.PreviewAPICommands.DISABLE_QUICK_TOOL_VERBS]: {
        action: this._disableQuickToolVerbs.bind(this)
      },
      [_APICommands.PreviewAPICommands.SHOW_QUICK_TOOL]: {
        validator: this._validateBooleanData.bind(this),
        action: this._showQuickTool.bind(this)
      },
      [_APICommands.PreviewAPICommands.SUBSCRIBE_QUICK_TOOL]: {
        validator: this._validateFunctionType.bind(this),
        action: this._subscribeQuickTool.bind(this)
      },
      [_APICommands.PreviewAPICommands.REGISTER_BEFORE_NAVIGATION_BAR_TOOL_ACTIVATED_CALLBACK]: {
        validator: this._validateHookData.bind(this),
        action: this._registerBeforeNavigationBarToolActivatedCallback.bind(this)
      },
      [_APICommands.PreviewAPICommands.OPEN_RHP_PANEL]: {
        action: this._openRHPPanel.bind(this)
      },
      [_APICommands.PreviewAPICommands.CLOSE_RHP_PANEL]: {
        action: this._closeRHPPanel.bind(this)
      },
      [_APICommands.PreviewAPICommands.SUBSCRIBE_TO_RHP]: {
        validator: this._validateFunctionType.bind(this),
        action: this._subscribeToRHP.bind(this)
      },
      [_APICommands.PreviewAPICommands.UNSUBSCRIBE_FROM_RHP]: {
        validator: this._validateFunctionType.bind(this),
        action: this._unsubscribeFromRHP.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_RHP_CONFIG]: {
        action: this._getRHPConfig.bind(this)
      },
      [_APICommands.PreviewAPICommands.ADD_RHP_UI_CONTROL]: {
        validator: this._validateRHPUIControl.bind(this),
        action: this._addRHPUIControl.bind(this)
      },
      [_APICommands.PreviewAPICommands.UPDATE_PREVIEW_APP_CONFIG]: {
        validator: this._validateStringData.bind(this),
        action: this._updatePreviewAppConfig.bind(this)
      },
      [_APICommands.PreviewAPICommands.SWITCH_TO_CNPDF]: {
        action: this._switchToCNPDF.bind(this)
      },
      [_APICommands.PreviewAPICommands.NOTIFY_ASSET_VERSION_UPDATE]: {
        action: this._notifyAssetVersionUpdate.bind(this)
      },
      [_APICommands.PreviewAPICommands.SHOW_CONFLICTING_FILE_DIALOG]: {
        action: this._showConflictingFileDialog.bind(this)
      },
      [_APICommands.PreviewAPICommands.IS_EDITING_ALLOWED]: {
        action: this._isEditingAllowed.bind(this)
      },
      [_APICommands.PreviewAPICommands.IS_COMMENTING_ALLOWED]: {
        action: this._isCommentingAllowed.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_FILE_NAME]: {
        action: this._setFileName.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_ALL_BOOKMARK]: {
        action: this._getBookmarks.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_ATTACHMENTS]: {
        action: this._getAttachments.bind(this)
      },
      [_APICommands.PreviewAPICommands.ZOOM_IN]: {
        action: this._zoomIn.bind(this)
      },
      [_APICommands.PreviewAPICommands.ZOOM_OUT]: {
        action: this._zoomOut.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_ZOOM_LIMIT]: {
        action: this._getZoomLimit.bind(this)
      },
      [_APICommands.PreviewAPICommands.RESET_CURSOR]: {
        action: this._resetCursor.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_DARK_PAGE_THEME_SWITCH_DISABLED]: {
        action: this._setDarkPageThemeSwitchDisabled.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_DOCUMENT_VIEW_THEME_CHANGE]: {
        action: this._setDocumentViewThemeChange.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_CURRENT_PAGE]: {
        action: this._getCurrentPage.bind(this)
      },
      [_APICommands.PreviewAPICommands.SHOW_HUD]: {
        action: this._showHUD.bind(this)
      },
      [_APICommands.PreviewAPICommands.HIDE_HUD]: {
        action: this._hideHUD.bind(this)
      },
      [_APICommands.PreviewAPICommands.SAVE_PDF]: {
        action: this._savePDF.bind(this)
      },
      [_APICommands.PreviewAPICommands.APPLY_CNPDF_UPDATES]: {
        action: this._applyCNPDFUpdates.bind(this)
      },
      [_APICommands.PreviewAPICommands.DOWNLOAD_PDF]: {
        action: this._downloadPDF.bind(this)
      },
      [_APICommands.PreviewAPICommands.CLEAR_FILE_DIRTY_STATUS]: {
        action: this._clearFileDirtyState.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_PDF_PERMISSIONS]: {
        action: this._getPDFPermissions.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_PDF_METADATA]: {
        action: this._getPDFMetadata.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_SEARCH_UI]: {
        action: this._getSearchUI.bind(this)
      },
      [_APICommands.PreviewAPICommands.REGISTER_SEARCH_ADDON_PANEL]: {
        validator: this._validateRegisterSearchAddOnPanel.bind(this),
        action: this._registerSearchAddOnPanel.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_BBOX_FOR_PAGE]: {
        validator: this._validateGetBBoxForPage.bind(this),
        action: this._getBBoxForPage.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_BBOX_CONTENT]: {
        validator: this._validateGetBBoxContent.bind(this),
        action: this._getBBoxContent.bind(this)
      },
      [_APICommands.PreviewAPICommands.REGISTER_STICKY_TOOLS_ADDON_PANEL]: {
        validator: this._validateRegisterStickyToolsAddOnPanel.bind(this),
        action: this._registerStickyToolsAddOnPanel.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_SELECTED_CONTENT]: {
        action: this._getSelectedContent.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_TEXT_FROM_DOC_RECTS]: {
        action: this._getTextFromDocRects.bind(this)
      },
      [_APICommands.PreviewAPICommands.REGISTER_BEFORE_A11Y_TREE_RENDER_CALLBACK]: {
        validator: this._validateRegisterBeforeA11yTreeRenderCallback.bind(this),
        action: this._registerBeforeA11yTreeRenderCallback.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_COMMAND_MANAGER]: {
        action: this._getCommandManager.bind(this)
      },
      [_APICommands.PreviewAPICommands.FORCE_SHOW_COMMENTING_TOOLS]: {
        validator: this._validateBooleanData.bind(this),
        action: this._forceShowCommentingTools.bind(this)
      },
      [_APICommands.PreviewAPICommands.FORCED_SHOW_FULL_WINDOW_MODAL]: {
        validator: this._validateBooleanData.bind(this),
        action: this._forceShowFullWindowModal.bind(this)
      },
      [_APICommands.PreviewAPICommands.START_READ_ALOUD]: {
        action: this._startReadAloud.bind(this)
      },
      [_APICommands.PreviewAPICommands.STOP_READ_ALOUD]: {
        action: this._stopReadAloud.bind(this)
      },
      [_APICommands.PreviewAPICommands.IS_READ_ALOUD_PAUSED]: {
        action: this._isReadAloudPaused.bind(this)
      },
      [_APICommands.PreviewAPICommands.PAUSE_READ_ALOUD]: {
        action: this._pauseReadAloud.bind(this)
      },
      [_APICommands.PreviewAPICommands.PLAY_READ_ALOUD]: {
        action: this._playReadAloud.bind(this)
      },
      [_APICommands.PreviewAPICommands.HAS_NEXT_PARAGRAPH]: {
        action: this._hasNextParagraph.bind(this)
      },
      [_APICommands.PreviewAPICommands.GO_TO_NEXT_PARAGRAPH]: {
        action: this._goToNextParagraph.bind(this)
      },
      [_APICommands.PreviewAPICommands.HAS_PREVIOUS_PARAGRAPH]: {
        action: this._hasPreviousParagraph.bind(this)
      },
      [_APICommands.PreviewAPICommands.GO_TO_PREVIOUS_PARAGRAPH]: {
        action: this._goToPreviousParagraph.bind(this)
      },
      [_APICommands.PreviewAPICommands.UPDATE_FILE_META_DATA]: {
        action: this._updateFileMetaData.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_VOICE_RATE]: {
        validator: this._validateNonNegativeNumberData.bind(this),
        action: this._setVoiceRate.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_VOICE_RATES]: {
        action: this._getVoiceRates.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_SELECTED_VOICE_RATE]: {
        action: this._getSelectedVoiceRate.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_VOICE_OPTION]: {
        validator: this._validateNonNegativeNumberData.bind(this),
        action: this._setVoiceOption.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_VOICE_OPTIONS]: {
        action: this._getVoiceOptions.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_SELECTED_VOICE_OPTION]: {
        action: this._getSelectedVoiceOption.bind(this)
      },
      [_APICommands.PreviewAPICommands.IS_FILE_PROTECTED]: {
        action: this._isFileProtected.bind(this)
      },
      [_APICommands.PreviewAPICommands.SHOW_NOTIFICATION]: {
        validator: this._validateStringOrComponent.bind(this),
        action: this._showNotification.bind(this)
      },
      [_APICommands.PreviewAPICommands.CLEAR_ALL_NOTIFICATIONS]: {
        action: this._clearAllNotifications.bind(this)
      },
      [_APICommands.PreviewAPICommands.CLEAR_NOTIFICATION]: {
        action: this._clearNotification.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_PAGE_THUMBNAIL]: {
        validator: this._validatePageThumbnailData.bind(this),
        action: this._getPageThumbnail.bind(this)
      },
      [_APICommands.PreviewAPICommands.START_CARET_MODE]: {
        action: this._startCaretMode.bind(this)
      },
      [_APICommands.PreviewAPICommands.STOP_CARET_MODE]: {
        action: this._stopCaretMode.bind(this)
      },
      [_APICommands.PreviewAPICommands.IS_PDF_JAVASCRIPT_ENABLED]: {
        action: this._isPDFJavaScriptEnabled.bind(this)
      },
      [_APICommands.PreviewAPICommands.EXECUTE_DOC_WILL_CLOSE_ACTIONS]: {
        action: this._executeDocWillCloseActions.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_VIEWER_RETARGETED_FILE_INFO]: {
        action: this._setViewerRetargetedFileInfo.bind(this)
      },
      [_APICommands.PreviewAPICommands.ENABLE_PDF_JAVASCRIPT]: {
        validator: this._validateBooleanData.bind(this),
        action: this._enablePDFJavaScript.bind(this)
      },
      [_APICommands.PreviewAPICommands.ENABLE_ACROFORM_VALIDATION_FORMATTING]: {
        validator: this._validateBooleanData.bind(this),
        action: this._enableAcroformValidationFormatting.bind(this)
      },
      [_APICommands.PreviewAPICommands.ENABLE_OVERPRINT_SUPPORT]: {
        validator: this._validateBooleanData.bind(this),
        action: this._enableOverprintSupport.bind(this)
      },
      [_APICommands.PreviewAPICommands.REGISTER_CONTEXT_MENU_HANDLER]: {
        validator: this._validateRegisterContextMenuHandler.bind(this),
        action: this._registerContextMenuHandler.bind(this)
      },
      [_APICommands.PreviewAPICommands.SUBSCRIBE_TO_PDF_EDIT_OPERATIONS]: {
        validator: this._validateFunctionType.bind(this),
        action: this._subscribeToPDFEditOperations.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_UPDATED_PDF_BUFFER]: {
        validator: this._validateBooleanData.bind(this),
        action: this._getUpdatedPDFBuffer.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_ASSET_INFO]: {
        action: this._setAssetInfo.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_DOCUMENT_ID]: {
        action: this._getDocumentId.bind(this)
      },
      [_APICommands.PreviewAPICommands.INITIAL_PAGE_LOADED]: {
        action: this._getInitialPageLoaded.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_VISIBLE_PAGE_PDF_RECT]: {
        validator: this._validateNonNegativeNumberData.bind(this),
        action: this._getVisiblePagePDFRect.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_TEXT_BETWEEN_PDF_POINTS]: {
        action: this._getTextBetweenPDFPoints.bind(this)
      },
      [_APICommands.PreviewAPICommands.REGISTER_OVERLAY]: {
        action: this._registerOverlay.bind(this)
      },
      [_APICommands.PreviewAPICommands.SHOW_OVERLAY]: {
        validator: this._validateStringData.bind(this),
        action: this._showOverlay.bind(this)
      },
      [_APICommands.PreviewAPICommands.HIDE_OVERLAY]: {
        validator: this._validateStringData.bind(this),
        action: this._hideOverlay.bind(this)
      },
      [_APICommands.PreviewAPICommands.TRANSFORM_PDF_TO_VIEW]: {
        action: this._transformPDFToView.bind(this)
      },
      [_APICommands.PreviewAPICommands.TRANSFORM_VIEW_TO_PDF]: {
        action: this._transformViewToPDF.bind(this)
      },
      [_APICommands.PreviewAPICommands.GET_PAGE_ATTRIBUTES]: {
        validator: this._validatePositiveNumberData.bind(this),
        action: this._getPageAttributes.bind(this)
      },
      [_APICommands.PreviewAPICommands.EXTRACT_PAGE_TEXT]: {
        action: this._extractPageText.bind(this)
      },
      [_APICommands.PreviewAPICommands.PRELOAD_EDIT]: {
        action: this._preloadEdit.bind(this)
      },
      [_APICommands.PreviewAPICommands.INSERT_TEXT]: {
        action: this._insertText.bind(this)
      },
      [_APICommands.PreviewAPICommands.REPLACE_TEXT]: {
        action: this._replaceText.bind(this)
      },
      [_APICommands.PreviewAPICommands.DELETE_TEXT]: {
        action: this._deleteText.bind(this)
      },
      [_APICommands.PreviewAPICommands.DISCARD_PREVIEW]: {
        action: this._discardPreview.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_BOTTOM_BOARD_PANEL]: {
        validator: this._validateBottomBoardPanelData.bind(this),
        action: this._setBottomBoardPanel.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_BOTTOM_BOARD_BANNER]: {
        action: this._setBottomBoardBanner.bind(this)
      },
      [_APICommands.PreviewAPICommands.SHOW_BOTTOM_BOARD_PANEL]: {
        validatior: this._validateBooleanData.bind(this),
        action: this._showBottomBoardPanel.bind(this)
      },
      [_APICommands.PreviewAPICommands.SHOW_BOTTOM_BOARD_BANNER]: {
        validatior: this._validateBooleanData.bind(this),
        action: this._showBottomBoardBanner.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_BOTTOM_BOARD_CONFIG]: {
        action: this._setBottomBoardConfig.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_FAB_VERB_ID]: {
        action: this._setFabVerbId.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_FAB_VISIBILITY]: {
        validatior: this._validateBooleanData.bind(this),
        action: this._setFabVisibility.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_TOP_PANEL_VERB_ID]: {
        action: this._setTopPanelVerbId.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_TOP_PANEL_VISIBILITY]: {
        validatior: this._validateBooleanData.bind(this),
        action: this._setTopPanelVisibility.bind(this)
      },
      [_APICommands.PreviewAPICommands.SET_TOP_PANEL_EXPANDED_STATE]: {
        validatior: this._validateBooleanData.bind(this),
        action: this._setTopPanelExpandedState.bind(this)
      },
      [_APICommands.PreviewAPICommands.ADD_FLOATING_WINDOW]: {
        validator: this._validateFloatingWindowData.bind(this),
        action: this.addWindow.bind(this)
      },
      [_APICommands.PreviewAPICommands.UPDATE_DOC_INFO]: {
        action: this._updateDocInfo.bind(this)
      }
    };
  }
  addWindow(params) {
    return this._publicAPIs.floatingWindows.addWindow(params);
  }
  _validateFloatingWindowData(data) {
    const {
      commandData
    } = data;
    let err;
    if (typeof commandData !== "object" || commandData === null) {
      return (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only object input is supported for command");
    }
    return err;
  }
  _switchPreview(clientCallback) {
    const appStore = _ServiceRegistry.default.getService("AppStore");
    const previewCallback = async () => {
      const {
        fileInfo,
        previewConfig
      } = await clientCallback();
      const actionConfig = appStore.getActionConfig("preview");
      appStore.configStore.setActionConfig("preview", _objectSpread(_objectSpread({}, actionConfig), previewConfig));
      appStore.initializeFileInfoList([fileInfo]);
      const fi = appStore.fileInfoList[0];
      appStore.setIsPreviewSwitching(true);
      appStore.setPreviewMetadata(fi);
      const info = appStore.previewMetadata;
      appStore.setFileLoaded(true);
      this._resetQuickTool();
      return {
        assetUri: info.id,
        docTitle: info.fileName
      };
    };
    return this._publicAPIs.app.switchPreview(previewCallback).finally(() => appStore.setIsPreviewSwitching(false));
  }
  static getClassName() {
    return "PreviewCommands";
  }
  _getPreviewAPIs() {
    if (this._oldAPIs && this._publicAPIs) {
      return Promise.resolve(true);
    }
    const commandList = ["modifyCursor", "document", "documentActions", "documentState", "reusableComponents", "commandManager", "contextMenu", "pageView"];
    return Promise.all([_ServiceRegistry.default.getService("AppStore").previewRef.getApis("INT_COMMANDS", commandList), _ServiceRegistry.default.getService("AppStore").previewRef.getPublicApis()]).then(apis => {
      this._oldAPIs = apis[0];
      this._publicAPIs = apis[1];
      return true;
    });
  }

  /**
   * @method
   * @description
   * it execute the command related to pdf viewer
   *
   * @param {Object} data having command, commandData and options
   */
  executeCommand(data) {
    var _data$options;
    const invalidError = this._validatePreviewCommand(data);
    if (invalidError) return Promise.reject(invalidError);
    if (data !== null && data !== void 0 && (_data$options = data.options) !== null && _data$options !== void 0 && _data$options.notWaitForLinearizationPromise) {
      return this._apiCommands[data.command].action.call(this, data.commandData);
    }
    return new Promise((resolve, reject) => {
      _ServiceRegistry.default.getService("AppStore").getLinearizationWaitPromise().then(() => {
        this._getPreviewAPIs().then(() => this._apiCommands[data.command].action.call(this, data.commandData, data.options)).then(resolve).catch(err => reject((0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.FAIL, err)));
      });
    });
  }
  _validateStringData(data) {
    const {
      commandData
    } = data;
    let err;
    if (typeof commandData !== "string") {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only String input is supported for command");
    }
    return err;
  }
  _validateSearchData(data) {
    const {
      commandData
    } = data;
    let err;
    if (commandData) {
      const {
        searchText
      } = commandData;
      if (typeof searchText !== "string" || searchText === "") {
        err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only String input is supported for Search Command");
      }
    }
    return err;
  }
  _validatePerformSearchData(data) {
    const {
      commandData
    } = data;
    const {
      text,
      startPage,
      numPages,
      priority
    } = commandData;
    let err;
    if (typeof text !== "string" || text === "") {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only String input is supported for text");
    } else if (typeof startPage !== "number" || startPage <= 0) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "startPage should be a non-negative number");
    } else if (typeof numPages !== "number" || numPages <= 0) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "numPages should be a positive number");
    } else if (typeof priority !== "number" || priority < 0) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "priority should be a non-negative number");
    }
    return err;
  }
  _validateBooleanData(data) {
    const {
      commandData
    } = data;
    let err;
    if (typeof commandData !== "boolean") {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only Boolean input is supported for command");
    }
    return err;
  }
  _validateGoToData(data) {
    const {
      commandData
    } = data;
    let err;
    if (!commandData || "pageIndex" in commandData && !(0, _AppUtil.isInt)(commandData.pageIndex) || "pageNumber" in commandData && (!(0, _AppUtil.isInt)(commandData.pageNumber) || commandData.pageNumber < 1) || !(0, _AppUtil.isInt)(commandData.left) || !(0, _AppUtil.isInt)(commandData.top)) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only valid pageNumber, left and top is supported for command");
    }
    return err;
  }
  _validateAddSpaceBelowDocumentData(data) {
    const {
      commandData
    } = data;
    let err;
    if (typeof commandData.space !== "number" || Number.isNaN(commandData.space)) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only number space value is supported");
    }
    return err;
  }
  _validateThumbnailsData(data) {
    const {
      commandData,
      options: {
        preCalculatedData
      }
    } = data;
    const options = commandData.options || {};
    const zoomData = preCalculatedData.zoomData;
    let err;
    const filterFunc = pageNumber => pageNumber < 1 || pageNumber > preCalculatedData.totalPageCount;
    if (!commandData || !commandData.pageNumbers || !(0, _AppUtil.isNonEmptyArray)(commandData.pageNumbers) || (0, _AppUtil.isNonEmptyArray)(commandData.pageNumbers.filter(filterFunc))) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_PAGE_RANGE, "The requested page range is not valid.");
    } else if ("zoomLevel" in options && (!(0, _AppUtil.isPositiveFloat)(options.zoomLevel) || options.zoomLevel < zoomData.minZoom || options.zoomLevel > zoomData.maxZoom)) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_ZOOM_LEVEL, "The requested zoom level is not valid.");
    } else if ("format" in options && THUMBNAIL_OPTIONS.THUMBNAIL_FORMATS.indexOf(options.format) === -1) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_THUMBNAIL_FORMAT, "The requested format is not valid.");
    }
    return err;
  }
  _validateNonNegativeNumberData(data) {
    const {
      commandData
    } = data;
    let err;
    if (!((0, _AppUtil.isInt)(commandData) && commandData >= 0)) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only positive number is supported for command");
    }
    return err;
  }
  _validatePositiveNumberData(data) {
    const {
      commandData
    } = data;
    let err;
    if (!((0, _AppUtil.isInt)(commandData) && commandData > 0)) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only positive number is supported for command");
    }
    return err;
  }
  _validatePositiveFloatData(data) {
    const {
      commandData
    } = data;
    let err;
    if (!(0, _AppUtil.isPositiveFloat)(commandData)) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only positive number is supported for command");
    }
    return err;
  }

  /**
   * @method
   * @description
   * it validates the preview command whether it is allowed.
   *
   * @return Return undefined if it is valid
   */
  _validatePreviewCommand(data) {
    if (this._apiCommands[data.command].validator) {
      return this._apiCommands[data.command].validator(data);
    }
    return undefined;
  }
  _validateXMPData(data) {
    let err;
    const {
      commandData
    } = data;
    if (commandData) {
      const {
        fields,
        rawStream
      } = commandData;
      if (fields !== undefined) {
        if (typeof fields !== "object" || fields.length === undefined) {
          err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only array input is supported for fields in command");
        }
        if (!err) {
          fields.forEach(field => {
            if (typeof field !== "string") {
              err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only string input is supported for fields value in command");
            }
          });
        }
      }
      if (!err && rawStream !== undefined && typeof rawStream !== "boolean") {
        err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only Boolean input is supported for rawStream in command");
      }
    }
    return err;
  }
  _validateFunctionType(data) {
    let err;
    const {
      commandData
    } = data;
    if (typeof commandData !== "function") {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only function is supported for command");
    }
    return err;
  }
  _validateHookData(data) {
    let err;
    const {
      commandData: {
        callbackFn,
        opts
      }
    } = data;
    if (typeof callbackFn !== "function") {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only function is supported in callback for command");
    }
    if (opts && typeof opts !== "object") {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only object is supported in options for command");
    }
    return err;
  }
  _validatePageThumbnailData(data) {
    let err;
    const {
      commandData
    } = data;
    const {
      pageNum,
      thumbnailRect
    } = commandData;
    if (!pageNum || !(0, _AppUtil.isInt)(pageNum) || pageNum <= 0) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only positive integer value is supported for pageNum in command");
    }
    if (!thumbnailRect || typeof thumbnailRect !== "object" || !thumbnailRect.height || !thumbnailRect.width || !(0, _AppUtil.isPositiveFloat)(thumbnailRect.height) || !(0, _AppUtil.isPositiveFloat)(thumbnailRect.width)) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "thumbnailRect is not valid. " + "thumbnailRect should be an object containing a positive value of height and width");
    }
    return err;
  }
  _validateRegisterContextMenuHandler(data) {
    let err;
    const {
      commandData: {
        callback,
        triggerType
      }
    } = data;
    if (typeof callback !== "function") {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only function is supported in callback for command");
    }
    const allowedContextMenuTriggerTypes = Object.values(_ApiConstants.default.CONTEXT_MENU_TRIGGER_TYPE);
    if (typeof triggerType !== "undefined" && !allowedContextMenuTriggerTypes.includes(triggerType)) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, `Trigger type is not valid. Valid values are: ${allowedContextMenuTriggerTypes.join()}`);
    }
    return err;
  }
  _validateRHPUIControl(data) {
    let err;
    const {
      commandData: {
        controls,
        position
      }
    } = data;
    if (!Array.isArray(controls)) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only arrays is supported in controls for command");
    }
    const allowedRHPPositionTypes = Object.values(_ApiConstants.default.RHP_POSITION_TYPE);
    if (typeof position !== "undefined" && !allowedRHPPositionTypes.includes(position)) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, `Trigger type is not valid. Valid values are: ${allowedRHPPositionTypes.join()}`);
    }
    return err;
  }
  _validateBottomBoardPanelData(data) {
    const {
      dropinId,
      props
    } = data.commandData;
    let err;
    if (typeof dropinId !== "string" || props && typeof props !== "object") {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, typeof dropinId !== "string" ? "Only String input valid" : "props should be object");
    }
    return err;
  }

  /**
   * @method
   * @description
   * it shows the undocked HUD if dock page controls is diabled and we can make it
   * show forcefully by passing param true
   *
   * @param {Boolean} force if it's true it show hud instead of dockPageControls configuration
   */
  _shouldShowUndockedHUD(force = false) {
    const previewConfig = _ServiceRegistry.default.getService("AppStore").getActionConfig("preview");
    if (!previewConfig.dockPageControls || force) {
      this._publicAPIs.hud.show(true);
    }
  }
  _customiseUIControl(property, visible) {
    this._publicAPIs.app.customizeUIControl(property, {
      visible
    });
    this._shouldShowUndockedHUD();
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * show or hide fit width icon in HUD
   *
   * @param {Boolean} visible fit width icon is visible if param is true
   */
  _toggleFitWidth(visible) {
    return this._customiseUIControl("FIT_WIDTH", visible);
  }

  /**
   * @method
   * @description
   * show or hide two column icon from RHP
   *
   * @param {Boolean} visible two column icon is visible if param is true
   */
  _toggleTwoColumn(visible) {
    return this._customiseUIControl("TWO_COLUMN", visible);
  }

  /**
   * @method
   * @description
   * show or hide full screen icon from RHP
   *
   * @param {Boolean} visible - full screen icon is visible if param is true
   */
  _toggleFullScreenButton(visible) {
    return this._customiseUIControl("FULL_SCREEN", visible);
  }

  /**
   * @method
   * @description
   * show or hide fit page icon in HUD
   *
   * @param {Boolean} visible fit page icon is visible if param is true
   */
  _toggleFitPage(visible) {
    return this._customiseUIControl("FIT_PAGE", visible);
  }

  /**
   * @method
   * @description
   * show or hide page navigation control icon in HUD
   *
   * @param {Boolean} visible page navigation icon is visible if param is true
   */
  _togglePageNavControl(visible) {
    return this._customiseUIControl("PAGE_NAVIGATION_CONTROL", visible);
  }

  /**
   * @method
   * @description
   * show or hide page number field
   *
   * @param {Boolean} visible page number field is visible if param is true
   */
  _togglePageNumControl(visible) {
    return this._customiseUIControl("PAGE_NUMBER_CONTROL", visible);
  }

  /**
   * @method
   * @description
   * show or hide zoom control icon in HUD
   *
   * @param {Boolean} visible zoom control icon is visible if param is true
   */
  _toggleZoomControl(visible) {
    return this._customiseUIControl("ZOOM_CONTROL", visible);
  }

  /**
   * @method
   * @description
   * force show or hide commenting tools
   *
   * @param {Boolean} visible commenting tools are visible if param is true
   */
  _forceShowCommentingTools(visible) {
    return this._customiseUIControl("FORCE_SHOW_COMMENTING_TOOLS", visible);
  }
  _forceShowFullWindowModal(visible) {
    if (_ServiceRegistry.default.getService("AppStore").isSizedPreset) {
      _ServiceRegistry.default.getService("AppStore").configStore.setShowFullScreenModal(visible);
    }
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * show or hide dock icon in HUD
   *
   * @param {Boolean} visible dock icon is visible if param is true
   */
  _toggleDockSwitch(visible) {
    return this._customiseUIControl("DOCK_SWITCH", visible);
  }

  /**
   * @method
   * @description
   * show or hide download icon in header section
   *
   * @param {Boolean} visible download icon is visible if param is true
   */
  _toggleDownload(visible) {
    const appStore = _ServiceRegistry.default.getService("AppStore");
    appStore.configStore.setShowDownload(visible);
    if (appStore.isInLinePreset || appStore.isSizedPreset) {
      return this._customiseUIControl("DOWNLOAD", visible);
    }
    // apiObject.app.enableFeature("DOWNLOAD", visible).catch(() => {});
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * if visible  is true it switched the view mode to full screen and update
   * the full screen icon in HUD
   *
   */
  _toggleFullScreen(visible) {
    const configStore = _ServiceRegistry.default.getService("AppStore").configStore;
    configStore.setShowFullScreen(visible);
    if (!configStore.getConfig().isViewSDKPublic) {
      _ServiceRegistry.default.getService("FullScreenManager").updateFSIconInHud();
    }
    this._shouldShowUndockedHUD(true);
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * show or hide print icon in header section
   *
   * @param {Boolean} visible print icon is visible if param is true
   */
  _togglePrint(visible) {
    const appStore = _ServiceRegistry.default.getService("AppStore");
    appStore.configStore.setShowPrint(visible);
    if (appStore.isInLinePreset || appStore.isSizedPreset) {
      return this._customiseUIControl("PRINT", visible);
    }
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * show or hide comments panel in the right hand panel
   *
   * @param {Boolean} visible HUD is visible if param is true
   */
  _toggleCommentPanel(visible) {
    const appStore = _ServiceRegistry.default.getService("AppStore");
    if (appStore.isFWPreset) {
      return this._customiseUIControl("COMMENTS_PANEL", visible);
    }
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * show or hide HUD at the bottom of screen
   *
   * @param {Boolean} visible HUD is visible if param is true
   */
  _toggleHUD(visible) {
    _ServiceRegistry.default.getService("AppStore").setIsHidingBranding(!visible);
    this._publicAPIs.hud.show(visible);
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * show or hide LHP at the left side of screen
   *
   * @param {Boolean} visible LHP is visible if param is true
   */
  _toggleLHP(visible) {
    this._publicAPIs.lhp.show(visible);
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * show or hide right hand panel
   *
   * @param {Boolean} visible right hand panel is visible if param is true
   */
  _toggleRHP(visible) {
    this._publicAPIs.rhp.show(visible);
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * make the legal dialog open
   *
   * @param {Boolean} value Legal Dialog visible if value is true
   */
  _setLegalDialogOpen(value) {
    const appStore = _ServiceRegistry.default.getService("AppStore");
    appStore.legalDialogOpen = value;
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * it makes the HUD docked at the bottom of screen
   */
  _dockHUD(dock) {
    this._publicAPIs.hud.fadeIn(0);
    this._publicAPIs.hud.dock(dock);
    return Promise.resolve(true);
  }
  _setSelectedAsset(selectedAsset) {
    const integrationProvider = _ServiceRegistry.default.getService("IntegrationProvider");
    integrationProvider.setSelectedAsset(Promise.resolve(selectedAsset));
    return Promise.resolve(true);
  }
  _setViewMode(viewMode) {
    let defaultViewMode = viewMode;
    const appStore = _ServiceRegistry.default.getService("AppStore");
    if ((0, _AppUtil.isValidMobileViewMode)(defaultViewMode) && !appStore.showViewMode) {
      defaultViewMode = "";
    }
    appStore.setCurrentViewMode(defaultViewMode);
    this._publicAPIs.document.setViewMode(_ViewMode.InternalViewMode[defaultViewMode] || defaultViewMode);
    return Promise.resolve(true);
  }
  _getViewMode() {
    return this._publicAPIs.document.getViewMode();
  }

  /**
   * @method
   * @description
   * exit full screen mode
   */
  _exitFullScreenMode() {
    return this._publicAPIs.document.exitFullScreenMode();
  }
  _toggleScrubber(visible) {
    return this._customiseUIControl("SCRUBBER", visible);
  }
  _toggleFileNameToast(visible) {
    const result = this._customiseUIControl("FILE_NAME_TOAST", visible);
    _ServiceRegistry.default.getService("EventsService").sendEvent(_Events.InternalEvents.FILE_NAME_TOAST_VISIBILITY, visible);
    return result;
  }

  /**
   * @method
   * @description
   * it show the HUD at the bottom of available screen
   */
  _showHUD() {
    this._publicAPIs.hud.show(true);
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * it hide the HUD from the available screen
   */
  _hideHUD() {
    this._publicAPIs.hud.show(false);
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * it sets the page cursor to any cursor style for the pdf view
   */
  _setCursor(data) {
    this._oldAPIs.modifyCursor.setCursor(data);
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * it revokes an applied cursor style and reverts it to the default cursor style.
   */
  _resetCursor() {
    this._oldAPIs.modifyCursor.resetCursor();
    return Promise.resolve(true);
  }
  _setDarkPageThemeSwitchDisabled(darkPageThemeSwitchDisabled) {
    this._publicAPIs.document.setDarkPageThemeSwitchDisabled(darkPageThemeSwitchDisabled);
    return Promise.resolve(true);
  }
  _setDocumentViewThemeChange(themeData) {
    this._publicAPIs.document.setDocumentViewThemeChange(themeData);
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * it returns the current page number in focus page
   */
  _getCurrentPage() {
    return this._publicAPIs.document.getCurrentPageNumber();
  }

  /**
   * @method
   * @description
   * it takes the PDF page number as input and returns the zoom level of that page.
   *
   * @param pageNumber page number of document
   */
  _getPageZoom(pageNumber) {
    return this._publicAPIs.document.getCurrentZoomLevel(pageNumber).then(zoom => {
      const normalizedZoom = zoom / 100;
      return (0, _AppUtil.fixedToDigits)(normalizedZoom, 3);
    });
  }
  _getPageAttributes(pageNumber) {
    return this._publicAPIs.document.getPageAttributes(pageNumber);
  }

  /**
   * It accepts a page number as input and returns the text and text attributes for that page
   * @param {Number} pageNumber - page number to extract data from
   * @return {Promise} - promise that resolves with extracted page text metadata
   */
  _extractPageText(pageNumber) {
    return this._publicAPIs.document.extractPageText(pageNumber);
  }

  /**
   * @method
   * @description
   * the document zooms in to the given zoom level such that it lies between minZoom and maxZoom
   *
   * @param level zoomLevel to be updated
   *
   * @return {Promise} return Promise resolved with current zoom level
   */
  _setZoomLevel(level) {
    const normalizedZoom = this._publicAPIs.document.zoomTo(level * 100);
    if (normalizedZoom < 0) {
      return Promise.reject("Zoom In/Out is disabled");
    }
    return Promise.resolve((0, _AppUtil.fixedToDigits)(normalizedZoom / 100, 3));
  }
  /**
   * @method
   * @description
   * the document zooms in to the next zoom level such that it's never greater than maxZoom
   *
   * @return {Promise} return Promise resolved with current zoom level
   */
  _zoomIn() {
    const normalizedZoom = (0, _AppUtil.fixedToDigits)(this._publicAPIs.document.zoomIn() / 100, 3);
    if (normalizedZoom < 0) {
      return Promise.reject("Zoom In/Out is disabled");
    }
    return Promise.resolve(normalizedZoom);
  }

  /**
   * @method
   * @description
   * the document zooms out to the previous zoom level such that it's never less than minZoom
   *
   * @return {Promise} return Promise resolved with current zoom level
   */
  _zoomOut() {
    const normalizedZoom = (0, _AppUtil.fixedToDigits)(this._publicAPIs.document.zoomOut() / 100, 3);
    if (normalizedZoom < 0) {
      return Promise.reject("Zoom In/Out is disabled");
    }
    return Promise.resolve(normalizedZoom);
  }

  /**
   * @method
   * @description
   * it returns the minimum and maximum allowed zoom levels of a document.
   *
   * @return {Promise} return Promise resolved with min and max zoom level
   */
  _getZoomLimit() {
    return new Promise((resolve, reject) => {
      this._publicAPIs.document.getZoomLimits().then(zoomData => {
        resolve({
          minZoom: (0, _AppUtil.fixedToDigits)(zoomData.minZoom / 100, 3),
          maxZoom: (0, _AppUtil.fixedToDigits)(zoomData.maxZoom / 100, 3)
        });
      }).catch(() => {
        reject("Zoom In/Out is disabled");
      });
    });
  }

  /**
   * @method
   * @description
   * it takes the search text as input and highlights the first search result in UI from the viewing page
   *
   * @param searchText string to be searched
   */
  _searchQuery({
    searchText
  }) {
    return new Promise((resolve, reject) => {
      const appStore = _ServiceRegistry.default.getService("AppStore");
      if (!appStore.getActionConfig("preview").enableSearchAPIs) {
        reject("Search APIs not enabled.");
        return;
      }
      this._publicAPIs.document.search(searchText).then(searchObject => resolve(new _SearchModel.default(searchObject).getExposedAPIs())).catch(error => reject(error));
    });
  }
  _performSearch({
    text,
    startPage,
    numPages,
    priority,
    zoomLevel
  }) {
    return new Promise((resolve, reject) => {
      this._publicAPIs.document.performSearch(text, startPage, numPages, priority, zoomLevel).then(searchresponse => resolve(searchresponse)).catch(error => reject(error));
    });
  }

  /**
   * @method
   * @description
   * it returns the thumbnail of the specified page in a document
   *
   * @param data
   * @param minZoom
   */
  _getThumbnails(data, {
    preCalculatedData: {
      zoomData: {
        minZoom
      }
    }
  }) {
    return new Promise((resolve, reject) => {
      const rejectPromise = error => {
        _ServiceRegistry.default.getService("LoggingService").info("PreviewCommands", "_getThumbnails", "Getting error from Rendition API", JSON.stringify(error));
        reject("There's some problem while processing your request.");
      };
      _dcCore.providers.rendition({}).then(renditionApi => {
        const appStore = _ServiceRegistry.default.getService("AppStore");
        const metaDataResponse = renditionApi.getMetadata(appStore.previewMetadata.id, {
          useAJS: true,
          useDCAPI: false
        });
        let zoomLevel = data.options.zoomLevel || THUMBNAIL_OPTIONS.THUMBNAIL_ZOOM_LEVEL;
        zoomLevel = zoomLevel > minZoom ? zoomLevel : minZoom;
        const response = {};
        metaDataResponse.ajs.then(() => {
          Promise.all(data.pageNumbers.map(pageNumber => {
            const thumbnailPromise = renditionApi.getRendition(appStore.previewMetadata.id, {
              pageNum: pageNumber,
              zoomLevel,
              useAJS: true,
              useDCAPI: false
            });
            thumbnailPromise.dcapi.catch(() => {});
            return thumbnailPromise.ajs.then(rendition => {
              const byteArray = new Uint8Array(rendition.params.pageImgBuffer);
              const blob = new Blob([byteArray], {
                type: rendition.params.mimeType
              });
              Object.assign(response, {
                [pageNumber]: URL.createObjectURL(blob)
              });
            });
          })).then(() => {
            resolve(response);
          }).catch(rejectPromise);
        }).catch(rejectPromise);
        metaDataResponse.dcapi.catch(() => {});
      }).catch(rejectPromise);
    });
  }

  /**
   * @method
   * @description
   * This API enables navigation to any PDF page. It accepts a page number as input. You can also pass the x
   * and y coordinates on the page as optional input parameters to enable navigation to a particular location
   * on the page. When no coordinates are passed, the default coordinates are (0, 0)
   *
   * @return {Promise} return Promise resolved with true
   */
  _gotoLocation(data) {
    let pageNumber;
    if ("pageIndex" in data) {
      pageNumber = data.pageIndex + 1;
    } else {
      pageNumber = data.pageNumber;
    }
    this._publicAPIs.document.goto(pageNumber, data.left, data.top, data.scrollOptions);
    return Promise.resolve(true);
  }
  _addBottomSpaceBelowDocumentInMobile(data) {
    this._publicAPIs.document.addBottomSpaceBelowDocumentInMobile(data.space, data.opts);
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * it accepts a page number as input and clears any text selection applied to the page
   *
   * @param {Number} pageNumber
   *
   * @return {Promise} return Promise resolved with true
   */
  _clearPageSelection(pageNumber) {
    const pageIndex = pageNumber - 1;
    this._oldAPIs.documentActions.clearPageSelection(pageIndex, pageIndex);
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * it provides all the permissions and security information related to document
   *
   *
   * @return {Promise} return Promise resolved with all document security information
   */
  _getPDFPermissions() {
    return this._oldAPIs.documentState.getDocumentSecurityInformation();
  }

  /**
   * @method
   * @description
   * it returns basic information about the PDF fetched from the PDF dictionary, such as the number
   * of pages and title of the PDF
   *
   * @return {Promise} return Promise resolved with metadata of file
   */
  _getPDFMetadata() {
    const metadata = _ServiceRegistry.default.getService("AppStore").pdfMetadata;
    if ("pdfTitle" in metadata) {
      return Promise.resolve(metadata);
    }
    return Promise.reject("Unable to fetch metadata");
  }

  /**
   * @method
   * @description
   * if a user selects any content in the viewer, then the selected content can be fetched using this API.
   */
  _getSelectedContent() {
    return new Promise((resolve, reject) => {
      this._publicAPIs.document.getSelectedContent().then(resolve).catch(() => reject("Selected content is not available"));
    });
  }

  /**
   * @method
   * @description
   * if a user selects any content in the viewer, then the selected content can be fetched using this API.
   */
  _getTextFromDocRects(start, end) {
    return new Promise((resolve, reject) => {
      this._publicAPIs.document.getTextFromDocRects(start, end).then(resolve).catch(() => reject("Selected content is not available"));
    });
  }

  /**
   * @method
   * @description
   * it checks the PDF for XMP metadata and returns it if present
   *
   * @return {Promise} return Promise resolved with XMP metadata of file
   */
  _getXMPMetadata(data) {
    return new Promise((resolve, reject) => {
      _dcCore.providers.rendition({}).then(renditionApi => {
        const appStore = _ServiceRegistry.default.getService("AppStore");
        const assetUri = appStore.previewMetadata.id;
        const xmpResponse = renditionApi.performCommand(assetUri, {
          assetMimeType: "application/pdf",
          assetType: "",
          commandName: "getDocumentXMPMetadata",
          enableExternal: "",
          useAJS: true,
          useDCAPI: false
        });
        xmpResponse.dcapi.catch(() => {});
        xmpResponse.ajs.then(response => {
          const {
            fields,
            rawStream
          } = data;
          const xmpStream = response.data.streamData;
          const result = {};
          if (rawStream) {
            result.xmpStream = xmpStream;
          }
          const fetchFields = fields && fields.length > 0 ? fields : ["dc:title", "dc:format", "xmpMM:DocumentID"];
          result.xmpMetadata = this._parseXMP(xmpStream, fetchFields);
          resolve(result);
        }).catch(error => {
          _ServiceRegistry.default.getService("LoggingService").error("PreviewCommands", "_getXMPMetadata", JSON.stringify(data), JSON.stringify(error));
          reject("Unable to fetch XMP metadata");
        });
      });
    });
  }
  _parseXMP(xmpStream, fields) {
    const response = {};
    fields.forEach(field => {
      response[field] = "";
    });
    if (xmpStream) {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmpStream, "text/xml");
      fields.forEach(field => {
        const tag = xmlDoc.getElementsByTagName(field);
        let value = "";
        if (tag && tag[0] && tag[0].textContent) {
          value = tag[0].textContent.trim();
        }
        response[field] = value;
      });
    }
    return response;
  }

  /**
   * @method
   * @description
   * it enable or disable the text selection features
   *
   * @param data
   */
  _toggleTextSelection(data) {
    const appStore = _ServiceRegistry.default.getService("AppStore");
    if (appStore.enableModernViewer && !appStore.shouldHideAnnotationTools && (appStore.isFWPreset || appStore.isIntegration)) {
      // Updating quick tool of modern viewer, if present
      this._publicAPIs.app.customizeUIControl("COMMENT_HIGHLIGHT_CONTROL", {
        visible: data
      });
      this._publicAPIs.app.customizeUIControl("COMMENT_UNDERLINE_CONTROL", {
        visible: data
      });
      this._publicAPIs.app.customizeUIControl("COMMENT_STRIKEOUT_CONTROL", {
        visible: data
      });
    }
    appStore.setTextSelectionEnabled(data);
    return this._publicAPIs.app.enableFeature("TEXT_SELECTION", data);
  }

  /**
   * @method
   * @description
   * it enables interactive/builtIn tooltips
   *
   */
  _enableInteractiveTooltip(data) {
    return this._publicAPIs.app.enableFeature("INTERACTIVE_TOOLTIP", data);
  }
  _applyCNPDFUpdates({
    manifest,
    addedComponents = [],
    updatedComponents = []
  }) {
    return this._oldAPIs.document.applyCNPDFUpdates({
      manifest,
      addedComponents,
      updatedComponents
    });
  }
  _savePDF() {
    return (0, _saveFile.default)().then(() => Promise.resolve(true)).catch(() => Promise.resolve(false));
  }
  _downloadPDF() {
    return (0, _downloadFile.default)().then(() => Promise.resolve(true)).catch(() => Promise.resolve(false));
  }
  _clearFileDirtyState() {
    _ServiceRegistry.default.getService("AppStore").setFileStatus(undefined);
    _ServiceRegistry.default.getService("AppStore").setIsDirty(false);
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * it get the search UI component that can be placed anywhere
   *
   * @return return with search UI react component
   */
  _getSearchUI() {
    return this._oldAPIs.reusableComponents.getSearchUI();
  }
  _validateRegisterSearchAddOnPanel({
    commandData
  }) {
    const {
      callback,
      getTripleDotMenuItems,
      trigger
    } = commandData;
    let err;
    if (typeof callback !== "function") {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only function is supported in callback for command");
    }
    const searchAddOnTrigger = Object.values(_ApiConstants.default.SEARCH_ADDON_TRIGGER);
    if (trigger && typeof trigger !== "string" && !searchAddOnTrigger.includes(trigger)) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only string is supported in trigger for command");
    }
    if (typeof getTripleDotMenuItems !== "function") {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only function is supported in getTripleDotMenuItems for command");
    }
    return err;
  }
  _validateRegisterStickyToolsAddOnPanel({
    commandData
  }) {
    let err;
    if (typeof commandData !== "function") {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only function is supported in callback for command");
    }
    return err;
  }
  _registerSearchAddOnPanel({
    callback,
    getTripleDotMenuItems,
    trigger
  }) {
    return this._publicAPIs.document.registerSearchAddOnPanel({
      callback,
      getTripleDotMenuItems,
      trigger
    });
  }
  _validateGetBBoxForPage({
    commandData
  }) {
    const {
      pageIndex
    } = commandData;
    let err;
    if (!(0, _AppUtil.isInt)(pageIndex) || pageIndex < 0) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "pageIndex should be a number");
    }
    return err;
  }
  _getBBoxForPage({
    pageIndex
  }) {
    return this._publicAPIs.document.getBBoxForPage({
      pageIndex
    });
  }
  _validateGetBBoxContent({
    commandData
  }) {
    const {
      pageIndex,
      itemIndex,
      bboxBounds
    } = commandData;
    let err;
    if (!(0, _AppUtil.isInt)(pageIndex) || pageIndex < 0) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "pageIndex should be a number");
    }
    if (!(0, _AppUtil.isInt)(itemIndex) || itemIndex < 0) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "itemIndex should be a number");
    }
    if (!bboxBounds || typeof bboxBounds !== "object" || !("l" in bboxBounds) || !("b" in bboxBounds) || !("r" in bboxBounds) || !("t" in bboxBounds)) {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "bboxBounds should be an object with { l, b, r, t }");
    }
    return err;
  }
  _getBBoxContent({
    pageIndex,
    itemIndex,
    bboxBounds
  }) {
    return this._publicAPIs.document.getBBoxContent({
      pageIndex,
      itemIndex,
      bboxBounds
    });
  }
  _registerStickyToolsAddOnPanel(callback) {
    return this._publicAPIs.stickyTools.registerStickyToolsAddOnPanel(callback);
  }
  _validateRegisterBeforeA11yTreeRenderCallback(data) {
    let err;
    const {
      callback
    } = data === null || data === void 0 ? void 0 : data.commandData;
    if (typeof callback !== "function") {
      err = (0, _ErrorUtils.commandError)(_ApiConstants.default.API_RESPONSE.INVALID_INPUT, "Only function is supported in callback for command");
    }
    return err;
  }
  _validateStringOrComponent(data) {
    var _data$prototype;
    if (typeof data === "string") {
      return true;
    }
    if (typeof data === "function" && (data.prototype instanceof _react.default.Component || typeof ((_data$prototype = data.prototype) === null || _data$prototype === void 0 ? void 0 : _data$prototype.render) === "function")) {
      return true;
    }
    return false;
  }
  _registerBeforeA11yTreeRenderCallback(data) {
    const {
      callback
    } = data;
    return this._publicAPIs.document.registerBeforeA11yTreeRenderCallback(callback);
  }

  /**
   * @method
   * @description
   * it exposes all the allowed command manager interface provided by preview APIs
   *
   * @return {Promise} - promise that resolves to command manager APIs object
   */
  _getCommandManager() {
    const commandManager = this._oldAPIs.commandManager;
    if (commandManager) {
      return Promise.resolve(commandManager);
    }
    return Promise.reject("Command Manager not found.");
  }

  /**
   * @method
   * @description
   * it registers callback with options for 'before RHP opens' with preview APIs
   *
   * @return {Promise} return Promise resolved with true
   */
  _registerBeforeRHPOpenCallback({
    callbackFn,
    opts
  }) {
    this._publicAPIs.app.getSideKickContainer().then(apis => {
      apis.beforeOpenPanel(callbackFn, opts);
    });
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * it registers callback with options for 'before quickTools gets activated' with preview APIs
   *
   * @return {Promise} return Promise resolved with true
   */
  _registerBeforeQuickToolActivatedCallback({
    callbackFn,
    opts
  }) {
    this._publicAPIs.quickTools.beforeToolActivated(callbackFn, opts);
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * it registers callback with options for 'before stickyTools gets activated' with preview APIs
   *
   * @return {Promise} return Promise resolved with true
   */
  _registerBeforeStickyToolActivatedCallback({
    callbackFn,
    opts
  }) {
    this._publicAPIs.stickyTools.beforeToolActivated(callbackFn, opts);
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * show coachMark for a verbId in preview dropin
   *
   * @param {String} dropinId - Id of the dropin for which coachMark will be shown
   * @return {Promise} return Promise resolved with true
   *
   */
  _showCoachMark(dropinId) {
    this._publicAPIs.FTE.showCoachMark(dropinId);
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * it resets the quick tool to default state using preview APIs
   *
   * @return {Promise} return Promise resolved with true
   */
  _resetQuickTool(options) {
    this._publicAPIs.quickTools.reset(options);
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * it returns the currently activated quick tool using preview APIs
   *
   * @return {Promise} return Promise resolved with true
   */
  _getActiveQuickTool() {
    return this._publicAPIs.quickTools.getActiveTool();
  }

  /**
   * @method
   * @description
   * Function to disable quick tool verbs and customize the UI control based on given configuration.
   * @param {Object} config {verb:"COMMENT_STICKYNOTE_CONTROL", disable:false, notificationMsg: "disabledMessage"}
   * @return {Promise} A promise that resolves with the outcome if the configuration is invalid.
   */
  _disableQuickToolVerbs(config) {
    if (!config || typeof config.verb === "undefined") {
      return this._publicAPIs.quickTools.disableQuickTools(config);
    }
    if (typeof config.verb !== "string" || !config.verb.trim()) {
      return Promise.reject(new Error("Invalid VerbId"));
    }
    return this._publicAPIs.app.customizeUIControl(config.verb, config);
  }

  /**
   * @method
   * @description
   * show or hide Quick Tool Panel at the left side of screen
   *
   * @param {Boolean} visible QuickTool Panel is visible if param is true
   */
  _showQuickTool(visible) {
    this._publicAPIs.quickTools.show(visible);
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * it registers callback with options for 'before navigatio bar tool activated' with preview APIs
   *
   * @return {Promise} return Promise resolved with true
   */
  _registerBeforeNavigationBarToolActivatedCallback({
    callbackFn,
    opts
  }) {
    if (this._publicAPIs.navigationBar) {
      this._publicAPIs.navigationBar.beforeNavigationBarToolActivated(callbackFn, opts);
    }
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * it expands the rhp with the tool whose ID is passed as argument, using preview APIs
   *
   * @param {String} toolId - Id of tool that should be present in expanded RHP
   * @return {Promise} return Promise resolved with true
   */
  _openRHPPanel(toolId, opts = {}) {
    return new Promise((resolve, reject) => {
      this._publicAPIs.app.getSideKickContainer().then(apis => {
        apis.openPanel(toolId, opts).then(() => resolve(true)).catch(() => reject("Unable to Open RHP panel"));
      });
    });
  }

  /**
   * @method
   * @description
   * it closes the quick tool to default state using preview APIs
   *
   * @return {Promise} return Promise resolved with true
   */
  _closeRHPPanel() {
    return new Promise((resolve, reject) => {
      this._publicAPIs.app.getSideKickContainer().then(apis => {
        apis.closePanel().then(() => resolve(true)).catch(() => reject("Unable to close RHP panel"));
      });
    });
  }

  /**
   * @method
   * @description
   * it registers a callback on RHP Dropin switch event, using preview APIs
   *
   * @param {Function} callback - Callback to register on Dropin Switch event of RHP
   * @return {Promise} return Promise resolved with true
   */
  _subscribeToRHP(callback) {
    return new Promise((resolve, reject) => {
      this._publicAPIs.app.getSideKickContainer().then(apis => {
        apis.subscribe(callback).then(() => resolve(true)).catch(() => reject("Unable to subscribe to RHP"));
      });
    });
  }

  /**
   * @method
   * @description
   * it unregisters a callback from RHP Dropin switch event, using preview APIs
   *
   * @param {Function} callback - Callback to unregister from Dropin Switch event of RHP
   * @return {Promise} return Promise resolved with true
   */
  _unsubscribeFromRHP(callback) {
    return new Promise((resolve, reject) => {
      this._publicAPIs.app.getSideKickContainer().then(apis => {
        apis.unsubscribe(callback).then(() => resolve(true)).catch(() => reject("Unable to unsubscribe from RHP"));
      });
    });
  }

  /**
   * @method
   * @description
   * it registers new verbs in Right hand panel, using preview APIs
   *
   * @param {Array} controls - array of ui controls(verbids, json object) to be shown in RHP panel
   * @param {Array} position - position where the verbs needs to be places. Valid values: "top", "bottom"
   * @return {Promise} return Promise resolved with true if success else rejects with error message
   */
  _addRHPUIControl({
    controls,
    position
  }) {
    return new Promise((resolve, reject) => {
      this._publicAPIs.rhp.addUIControl({
        controls,
        position
      }).then(() => resolve(true)).catch(() => reject("Unable to add UI in RHP"));
    });
  }

  /**
   * @method
   * @description
   * it checks whether the document can be edited or not depending upon if the document is
   * password protected, encrypted, corrupt or a PDF form
   *
   * @return {Promise} return Promise resolved/rejected with appropriate failureType
   */
  _isEditingAllowed() {
    return new Promise((resolve, reject) => {
      this._publicAPIs.document.isEditingAllowed().then(response => resolve(response)).catch(exception => reject(exception));
    });
  }

  /**
   * @method
   * @description
   * it checks whether the document can be edited or not depending upon if the document is
   * password protected, encrypted, corrupt or a PDF form
   *
   * @return {Promise} return Promise resolved/rejected with appropriate failureType
   */
  _isCommentingAllowed() {
    return new Promise((resolve, reject) => {
      this._publicAPIs.document.isCommentingAllowed().then(response => resolve(response)).catch(exception => reject(exception));
    });
  }

  /**
   * @method
   * @description
   * updates the doc info properties and adds new key value pairs inside PDF info dictionary
   *
   * @return {Promise} return Promise resolved/rejected with appropriate failureType
   */
  _updateDocInfo(data) {
    return new Promise((resolve, reject) => {
      this._publicAPIs.document.updateDocInfo(data).then(response => resolve(response)).catch(exception => reject(exception));
    });
  }

  /**
   * @method
   * @description
   * it updates modifed filename in viewer into preview document stores.
   *
   * @param {string} fileName Updated FileName
   */
  _setFileName(fileName) {
    const appStore = _ServiceRegistry.default.getService("AppStore");
    appStore.setFileName(fileName);
    this._publicAPIs.document.setFileName(fileName);
  }

  /**
   * @method
   * @description
   * it returns the currently expanded rhp tool's config using preview APIs
   *
   * @return {Promise} return Promise resolved with true
   */
  _getRHPConfig() {
    return new Promise((resolve, reject) => {
      this._publicAPIs.rhp.getConfig().then(config => resolve(config)).catch(() => reject("Unable to get RHP config"));
    });
  }

  /**
   * @method
   * @description
   * method to update preview app config
   *
   * @param {String} newAppConfig - Stringified app config
   *
   */
  _updatePreviewAppConfig(newAppConfig) {
    this._publicAPIs.app.updateAppConfig(newAppConfig);
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * Method to switch to CNPDF only after ajsMetadata is successfully fetched via PDF
   *
   * @param {Object} cnpdfFetcher - CNPDF fetcher object to fetch the cnpdf
   *
   */
  _switchToCNPDF(cnpdfFetcher) {
    return this._publicAPIs.document.switchToCNPDF(cnpdfFetcher).then(response => {
      _ServiceRegistry.default.getService("AppStore").configStore.updateActionConfig("preview", {
        enableCNPDF: true
      });
      return response;
    });
  }

  /**
   * @method
   * @description
   * Method to notify asset version update to preview app. It is currently called from saveFile.js
   * @param {Object} event - Event object
   * @return {any} - Returns the response from the notifyAssetVersionUpdate method
   */
  _notifyAssetVersionUpdate(event) {
    return this._publicAPIs.document.notifyAssetVersionUpdate(event);
  }

  /**
   * @method
   * @description
   * Shows a dialog for conflicting file with user information
   * @param {Object} params - Contains user information for the conflicting file
   * @param {String} params.mail - Email address of the user with conflicting changes
   * @param {String} params.name - Name of the user with conflicting changes
   * @return {Promise} - Promise that resolves when the conflicting file dialog is shown
   */
  _showConflictingFileDialog(params) {
    const dialogApi = _ServiceRegistry.default.getService("DialogApi");
    dialogApi.showNewFileDialog(params);
    return Promise.resolve();
  }

  /**
   * @method
   * @description
   * it checks whether the document is protected or not. A document is protected when it's
   * either password protected or encrypted
   *
   * @return {Promise} return Promise resolved/rejected with true
   */
  _isFileProtected() {
    return new Promise((resolve, reject) => {
      this._publicAPIs.document.isFileProtected().then(response => resolve(response)).catch(exception => reject(exception));
    });
  }
  _getPageThumbnail({
    pageNum,
    thumbnailRect
  }) {
    return new Promise((resolve, reject) => {
      this._publicAPIs.document.getPageThumbnail(pageNum, thumbnailRect).then(response => resolve(response)).catch(exception => reject(exception));
    });
  }
  _startReadAloud() {
    return this._publicAPIs.document.readAloud.start();
  }
  _stopReadAloud() {
    this._publicAPIs.document.readAloud.stop();
    return Promise.resolve(true);
  }
  _isReadAloudPaused() {
    return this._publicAPIs.document.readAloud.isPaused();
  }
  _pauseReadAloud() {
    return this._publicAPIs.document.readAloud.pause();
  }
  _playReadAloud() {
    return this._publicAPIs.document.readAloud.play();
  }
  _hasNextParagraph() {
    return this._publicAPIs.document.readAloud.hasNext();
  }
  _goToNextParagraph() {
    return this._publicAPIs.document.readAloud.next();
  }
  _hasPreviousParagraph() {
    return this._publicAPIs.document.readAloud.hasPrev();
  }
  _goToPreviousParagraph() {
    return this._publicAPIs.document.readAloud.prev();
  }
  _setVoiceRate(index) {
    return this._publicAPIs.document.readAloud.setVoiceRate(index);
  }
  _getSelectedVoiceRate() {
    return this._publicAPIs.document.readAloud.getSelectedVoiceRate();
  }
  _getVoiceRates() {
    return this._publicAPIs.document.readAloud.getVoiceRates();
  }
  _setVoiceOption(index) {
    return this._publicAPIs.document.readAloud.setVoiceOption(index);
  }
  _getVoiceOptions() {
    return this._publicAPIs.document.readAloud.getVoiceOptions();
  }
  _getSelectedVoiceOption() {
    return this._publicAPIs.document.readAloud.getSelectedVoiceOption();
  }

  /**
  * @method
  * @description
  * it registers a callback on Quick tools onVerbChange event
  *
  * @param {function} handler method on verb change event
  */
  _subscribeQuickTool(handler) {
    this._publicAPIs.quickTools.subscribe(handler);
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * It is used to show notification (toast or banner) through preview api
   *
   * @param {string} message Notification text message
   * @param {Object} options Notification options
   * @returns {Promise} Promise resolved with notification id
   */
  _showNotification(message, options) {
    return this._publicAPIs.document.showNotification(message, options);
  }

  /**
   * @method
   * @description
   * It clears all notifications (both toasts and banners)
   */
  _clearAllNotifications() {
    this._publicAPIs.document.clearAllNotifications();
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * It clears a specific notification (toast or banner) by id
   * @param {string} notificationId Notification id
   * @returns {Promise} Promise resolved with true if notification is cleared successfully
   * */
  _clearNotification(notificationId) {
    return this._publicAPIs.document.clearNotification(notificationId);
  }

  /**
   * @method
   * @description
   * * method to start caret mode
   */
  _startCaretMode() {
    return this._publicAPIs.document.caretMode.start();
  }

  /**
   * @method
   * @description
   * * method to stop caret mode
   */
  _stopCaretMode() {
    return this._publicAPIs.document.caretMode.stop();
  }
  _isPDFJavaScriptEnabled() {
    return this._publicAPIs.document.isPDFJavaScriptEnabled();
  }

  /**
   * @method
   * @description
   * execute Doc/WillClose actions
   */
  _executeDocWillCloseActions() {
    return this._publicAPIs.document.executeDocWillCloseActions();
  }

  /**
   * @method
   * @description
   * API to retarget Viewer and Preview to the newly created copied asset.
   * @param fileInfo fileInfo of copied asset.
   */

  _setViewerRetargetedFileInfo(fileInfo) {
    const appStore = _ServiceRegistry.default.getService("AppStore");
    appStore.initializeFileInfoList([fileInfo]);
    const fileInfoList = appStore.fileInfoList;
    appStore.setPreviewMetadata(fileInfoList[0]);
    return Promise.resolve(true);
  }

  /**
   * @method
   * @description
   * API to update file metadata
   * @param fileMetadata file metadata which is to be updated
   */

  _updateFileMetaData(fileMetadata) {
    const appStore = _ServiceRegistry.default.getService("AppStore");
    const updatedMetadata = _objectSpread(_objectSpread({}, appStore.fileInfoList[0].metaData), fileMetadata);
    appStore.setFileMetadata(appStore.fileInfoList[0].id, updatedMetadata);
    this._updateFileAccess(updatedMetadata.hasReadOnlyAccess);
    return true;
  }
  _updateFileAccess(hasReadOnlyAccess) {
    const appStore = _ServiceRegistry.default.getService("AppStore");
    appStore.previewRef.getPublicApis().then(apis => {
      const customizeUIControl = apis.app.customizeUIControl;
      const controls = ["COMMENT_STICKYNOTE_CONTROL", "COMMENT_TEXT_CONTROL", "COMMENT_HIGHLIGHT_CONTROL", "COMMENT_UNDERLINE_CONTROL", "COMMENT_STRIKEOUT_CONTROL", "COMMENT_DRAW_CONTROL", "COMMENT_INSERT_CONTROL", "COMMENT_REPLACE_CONTROL"];
      // eslint-disable-next-line no-restricted-syntax
      for (const control of controls) {
        customizeUIControl(control, {
          visible: !hasReadOnlyAccess
        });
      }
    });
  }

  /**
   * @method
   * @description
   * enable or disable the PDF JavaScript feature
   *
   * @param {Boolean} enable PDF JavaScript enabled if param is true.
   */
  _enablePDFJavaScript(enable) {
    return this._publicAPIs.app.enableFeature("PDF_JAVASCRIPT", enable);
  }

  /**
   * @method
   * @description
   * API to Enable or Disable Acroform Validation and Formatting. This is in effect if PDF JavaScript is disabled
   * @param {Boolean} shouldEnable
   */

  _enableAcroformValidationFormatting(shouldEnable) {
    return this._publicAPIs.app.enableFeature("ACROFORM_VALIDATION_FORMATTING", shouldEnable);
  }

  /**
   * @method
   * @description
   * enable or disable the Overprint support for page rendition
   *
   * @param {Boolean} enable true/false to enable/disable overprint support
   */
  _enableOverprintSupport(enable) {
    return this._publicAPIs.app.enableFeature("OVERPRINT_SUPPORT", enable);
  }

  /**
  * @method
  * @description
  * Registers a handler to add items to the context menu.
  *
  * @param {Function} callback A function that returns a promise resolved to an array of menu item objects.
  * @param {String} triggerType The trigger type of the context menu to subscribe to.
  * Possible values are "rightClick" and "textSelection". (Default: "rightClick")
  * @returns {Object} The subscription object for the context menu handler.
  */
  _registerContextMenuHandler({
    callback,
    triggerType = _ApiConstants.default.CONTEXT_MENU_TRIGGER_TYPE.RIGHT_CLICK
  }) {
    return this._oldAPIs.contextMenu.subscribe({
      name: "ViewSDK",
      contextMenuHandler: callback,
      type: triggerType
    });
  }
  _subscribeToPDFEditOperations(callback) {
    this._oldAPIs.document.subscribeToPDFEditOperations(callback);
  }
  _getUpdatedPDFBuffer(incremental) {
    return this._oldAPIs.document.getUpdatedPDFBuffer(incremental);
  }
  _setAssetInfo(assetInfo) {
    _ServiceRegistry.default.getService("AppStore").setAssetInfo(assetInfo);
  }
  _getDocumentId() {
    return this._oldAPIs.documentState.getDocumentId();
  }
  _getInitialPageLoaded() {
    return this._oldAPIs.documentState.initialPageLoaded();
  }
  _getVisiblePagePDFRect(pageNumber) {
    return this._oldAPIs.documentState.getVisiblePageRectInPDFView(pageNumber - 1);
  }
  _getTextBetweenPDFPoints({
    startPoint,
    endPoint,
    richText = false
  }) {
    return this._publicAPIs.document.getTextFromDocRects(startPoint, endPoint, richText);
  }
  _registerOverlay({
    clientName,
    overlayComponent,
    api,
    options
  }) {
    return this._publicAPIs.document.registerOverlay(`${_ApiConstants.default.EXTERNAL_OVERLAY_PREFIX}${clientName}`, overlayComponent, api, options);
  }
  _showOverlay(clientName) {
    return this._publicAPIs.document.showOverlay(`${_ApiConstants.default.EXTERNAL_OVERLAY_PREFIX}${clientName}`);
  }
  _hideOverlay(clientName) {
    return this._publicAPIs.document.hideOverlay(`${_ApiConstants.default.EXTERNAL_OVERLAY_PREFIX}${clientName}`);
  }
  _transformPDFToView({
    x,
    y,
    pageNum
  }) {
    const pageIndex = pageNum - 1;
    return this._oldAPIs.pageView.transformPDFToView(x, y, pageIndex);
  }
  _transformViewToPDF({
    x,
    y,
    pageNum
  }) {
    const pageIndex = pageNum - 1;
    return this._oldAPIs.pageView.transformViewToPDF(x, y, pageIndex);
  }
  _preloadEdit(opts) {
    return this._publicAPIs.document.edit.preloadEdit(opts);
  }
  _insertText({
    text,
    clickPoint,
    pageIndex,
    opts
  }) {
    return this._publicAPIs.document.edit.insertText(text, clickPoint, pageIndex, opts);
  }
  _replaceText({
    text,
    quads,
    pageIndex,
    opts
  }) {
    return this._publicAPIs.document.edit.replaceText(text, quads, pageIndex, opts);
  }
  _deleteText({
    quads,
    pageIndex,
    opts
  }) {
    return this._publicAPIs.document.edit.deleteText(quads, pageIndex, opts);
  }
  _discardPreview() {
    return this._publicAPIs.document.edit.discardPreview();
  }
  _setBottomBoardPanel({
    dropinId,
    props
  }) {
    return this._publicAPIs.bottomBoard.setPanel(dropinId, props);
  }
  _setBottomBoardBanner(component) {
    return this._publicAPIs.bottomBoard.setBanner(component);
  }
  _showBottomBoardPanel({
    val,
    overrideVisibility
  }) {
    return this._publicAPIs.bottomBoard.showPanel(val, overrideVisibility);
  }
  _showBottomBoardBanner(val) {
    return this._publicAPIs.bottomBoard.showBanner(val);
  }
  _setBottomBoardConfig(config) {
    return this._publicAPIs.bottomBoard.setConfig(config);
  }
  _setFabVerbId({
    verbId,
    opts
  }) {
    return this._publicAPIs.FAB.registerFAB(verbId, opts);
  }
  _setFabVisibility(val) {
    return this._publicAPIs.FAB.setVisibility(val);
  }
  _setTopPanelVerbId({
    verbId,
    opts
  }) {
    return this._publicAPIs.topPanel.registerTopPanel(verbId, opts);
  }
  _setTopPanelVisibility(val) {
    return this._publicAPIs.topPanel.setVisibility(val);
  }
  _setTopPanelExpandedState(val) {
    return this._publicAPIs.topPanel.setExpandedState(val);
  }
}
exports.default = PreviewCommands;